import React, { PropsWithChildren, ReactElement, useEffect, useState } from "react";
import Select, { OptionsType } from "react-select";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import merge from "lodash/merge";
import { SelectProps } from "./helpers";
import { PlaceHolder, customStyles, MultiSelectWrapper } from "./styles";
import { Menu, Option, ValueContainer } from "./CustomComponents";
import { SelectDropdownMaxHeight } from "../variables";

export function SelectComponent<D extends object>({
    className,
    multiSelectWrapperClassName,
    components,
    dropdownColor,
    formatGroupLabel,
    getOptionLabel,
    isClearable,
    isDisabled,
    isMulti,
    menuPlacement,
    menuPortalTarget,
    noOptionsMessage,
    onChange,
    options,
    placeholder,
    placeholderColor,
    secondaryStyles,
    value,
    isSearchable,
    defaultValue,
    onMenuClose,
    onMenuOpen,
    isMultiNewUI,
    hideSelectedOptions,
    isDuplicated,
    isOptionDisabled,
    shouldDisable
}: PropsWithChildren<SelectProps<D>>): ReactElement {
    const { t } = useTranslation();
    const customSelectStyles = merge({}, customStyles, secondaryStyles);

    const [dropDownMenuHeight, setdropDownMenuHeight] = useState<string>(
        `${SelectDropdownMaxHeight}px`
    );

    const [isDropDownMenuOpen, setisDropDownMenuOpen] = useState<boolean>(false);

    const selectAllHandleClick = (selectedValues: OptionsType<D>): void => {
        if (onChange) {
            onChange(selectedValues);
        }
    };
    let updatedOptions: { label: string; value: number }[] = [];

    const handleMenuOpen = () => {
        setisDropDownMenuOpen(true);
        if (onMenuOpen) {
            onMenuOpen();
        }
    };

    const handleMenuClose = () => {
        setisDropDownMenuOpen(false);
        if (onMenuClose) {
            onMenuClose();
        }
    };

    if (isDuplicated) {
        const data = new Map();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options?.forEach((i: any) => {
            data.set(i.value, i.label);
        });
        updatedOptions = Array.from(data, ([key, value]) => ({
            label: t(value),
            value: key
        }));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function translateOptions(options: any) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return options.map((option: any) => {
            if (option.label && typeof option.label === "string") {
                return {
                    ...option,
                    label: t(`${option.label}`)
                };
            }

            return option;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function translateValue(value: any) {
        if (value && value.label) {
            return {
                ...value,
                label: t(`${value.label}`)
            };
        }

        return value;
    }

    useEffect(() => {
        const removeAriaAutocomplete = () => {
            const inputs = document.querySelectorAll("input");

            inputs.forEach((input) => {
                // Removing the aria-autocomplete attribute because there is a conflict with the autocomplete prop which is coming for selectComponent
                input.removeAttribute("aria-autocomplete");
            });
        };

        // Running the function initial
        removeAriaAutocomplete();

        // Optionally, we are setting an interval to ensure it runs periodically
        const intervalId = setInterval(removeAriaAutocomplete, 1000);

        // Clearing interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    const handleZoom = () => {
        let zoomLevel = 0;
        const newZoomLevel = window.devicePixelRatio;

        if (zoomLevel !== newZoomLevel) {
            zoomLevel = newZoomLevel;
        }

        if (zoomLevel <= 1) {
            setdropDownMenuHeight(`${SelectDropdownMaxHeight}px`);
        } else {
            setdropDownMenuHeight(`${(SelectDropdownMaxHeight / zoomLevel) * 0.8}px`);
        }
    };

    useEffect(() => {
        const rootElement = document.querySelector("#root");

        if (rootElement) {
            handleZoom();

            window.addEventListener("resize", handleZoom);
        }

        return () => {
            window.removeEventListener("resize", handleZoom);
        };
    }, [isDropDownMenuOpen]);

    return (
        <MultiSelectWrapper
            className={multiSelectWrapperClassName}
            dropdownMenuHeight={dropDownMenuHeight}
            ismenuIsOpen={isDropDownMenuOpen}
        >
            <Select
                aria-label={typeof placeholder === "string" ? t(placeholder) : t("Select")}
                className={className}
                classNamePrefix="custom-multiselect"
                closeMenuOnSelect={!isMulti}
                components={
                    isMultiNewUI
                        ? {
                              ...components,
                              Menu,
                              Option,
                              ValueContainer
                          }
                        : components
                }
                defaultValue={translateValue(defaultValue)}
                dropdownColor={dropdownColor}
                filterOption={(option, searchText) => {
                    if (typeof option.label !== typeof "") {
                        return true;
                    }

                    return (
                        option.label.toLowerCase().includes(searchText.toLowerCase()) &&
                        option.label !== "hidden"
                    );
                }}
                formatGroupLabel={formatGroupLabel}
                getOptionLabel={getOptionLabel}
                hideSelectedOptions={isMultiNewUI ? hideSelectedOptions : isMulti}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isMulti={isMulti}
                isOptionDisabled={isOptionDisabled}
                isSearchable={isSearchable}
                menuPlacement={menuPlacement}
                menuPortalTarget={menuPortalTarget}
                noOptionsMessage={(msg): string | null => t(noOptionsMessage) || t(msg.inputValue)}
                options={
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    isDuplicated ? (updatedOptions as OptionsType<any>) : translateOptions(options)
                }
                placeholder={
                    typeof placeholder === "string" ? (
                        <PlaceHolder color={placeholderColor}>{t(`${placeholder}`)}</PlaceHolder>
                    ) : (
                        placeholder
                    )
                }
                secondaryStyles={secondaryStyles}
                selectAllHandleClick={selectAllHandleClick}
                shouldDisable={shouldDisable}
                styles={customSelectStyles}
                value={translateValue(value)}
                onChange={onChange}
                onMenuClose={handleMenuClose}
                onMenuOpen={handleMenuOpen}
            />
        </MultiSelectWrapper>
    );
}

SelectComponent.defaultProps = {
    hideSelectedOptions: false,
    isDisabled: false,
    isDuplicated: false,
    isMulti: false,
    isSearchable: true,
    menuPlacement: "auto",
    noOptionsMessage: i18next.t("No options"),
    options: [],
    placeholder: i18next.t("Select"),
    shouldDisable: false
};
